/** XeAgents.ai**/
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Phone, Plus, Trash2, RefreshCw } from "lucide-react";
import { AddSystemNumberDialog } from "./AddSystemNumberDialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatDate } from "@/lib/date-format";

interface PhoneNumber {
  id: string;
  phoneNumber: string;
  isSystemPool: boolean;
  userId?: string;
  userEmail?: string;
  purchasePrice?: number;
  monthlyPrice?: number;
  createdAt: string;
}

export default function PhoneNumbers() {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [syncingToElevenLabs, setSyncingToElevenLabs] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [clearingSyncStatus, setClearingSyncStatus] = useState(false);
  const { toast } = useToast();
  
  const { data: phoneNumbers, isLoading } = useQuery<PhoneNumber[]>({
    queryKey: ["/api/admin/phone-numbers"],
  });

  const handleSyncToElevenLabs = async () => {
    setSyncingToElevenLabs(true);
    try {
      const response = await apiRequest("POST", "/api/admin/phone-numbers/sync-to-elevenlabs");
      const result: {
        total?: number;
        success?: number;
        failed?: number;
        successes?: string[];
        errors?: string[];
        message?: string;
      } = await response.json();

      const total = result.total || 0;
      const success = result.success || 0;
      const failed = result.failed || 0;

      // Invalidate phone numbers query to refresh the table
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/phone-numbers"] });

      // Handle mixed outcomes
      if (success > 0 && failed > 0) {
        toast({
          title: "Partially Synced",
          description: `Synced ${success} of ${total} phone numbers to ElevenLabs. ${failed} failed. ${result.errors ? `Errors: ${result.errors.slice(0, 3).join(", ")}${result.errors.length > 3 ? "..." : ""}` : ""}`,
          variant: "default",
        });
      } else if (success > 0) {
        toast({
          title: "Sync Complete",
          description: `Successfully synced ${success} phone number${success > 1 ? "s" : ""} to ElevenLabs.`,
        });
      } else if (failed > 0) {
        toast({
          title: "Sync Failed",
          description: result.errors ? result.errors.join(", ") : `Failed to sync ${failed} phone numbers.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Already Synced",
          description: result.message || "All phone numbers are already synced to ElevenLabs.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to sync phone numbers to ElevenLabs",
        variant: "destructive",
      });
    } finally {
      setSyncingToElevenLabs(false);
    }
  };

  const handleCleanup = async () => {
    setCleaningUp(true);
    try {
      const response = await apiRequest("POST", "/api/admin/phone-numbers/cleanup");
      const result: {
        total?: number;
        removed?: number;
        kept?: number;
        removed_numbers?: string[];
        errors?: string[];
        message?: string;
      } = await response.json();

      const total = result.total || 0;
      const removed = result.removed || 0;
      const kept = result.kept || 0;

      // Invalidate phone numbers query to refresh the table
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/phone-numbers"] });

      // Show results
      if (removed > 0) {
        toast({
          title: "Cleanup Complete",
          description: `Removed ${removed} orphaned phone number${removed > 1 ? "s" : ""} that no longer exist with the provider. Kept ${kept} valid number${kept !== 1 ? "s" : ""}.`,
        });
      } else if (total > 0) {
        toast({
          title: "No Cleanup Needed",
          description: `All ${total} phone numbers are valid.`,
        });
      } else {
        toast({
          title: "No Numbers Found",
          description: result.message || "No phone numbers in database.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cleanup phone numbers",
        variant: "destructive",
      });
    } finally {
      setCleaningUp(false);
    }
  };

  const handleClearSyncStatus = async () => {
    setClearingSyncStatus(true);
    try {
      const response = await apiRequest("POST", "/api/admin/phone-numbers/clear-sync-status");
      const result: {
        total?: number;
        cleared?: number;
        message?: string;
      } = await response.json();

      const cleared = result.cleared || 0;

      // Invalidate phone numbers query to refresh the table
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/phone-numbers"] });

      // Show success message
      if (cleared > 0) {
        toast({
          title: "Sync Status Cleared",
          description: result.message || `Cleared ElevenLabs sync status for ${cleared} phone number${cleared > 1 ? "s" : ""}. You can now sync them again.`,
        });
      } else {
        toast({
          title: "No Numbers Found",
          description: result.message || "No phone numbers in database.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to clear sync status",
        variant: "destructive",
      });
    } finally {
      setClearingSyncStatus(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const systemPoolNumbers = phoneNumbers?.filter(p => p.isSystemPool) || [];
  const userNumbers = phoneNumbers?.filter(p => !p.isSystemPool) || [];
  const totalPurchaseCost = systemPoolNumbers.reduce((sum, p) => sum + (parseFloat(p.purchasePrice as any) || 0), 0);
  const totalMonthlyCost = systemPoolNumbers.reduce((sum, p) => sum + (parseFloat(p.monthlyPrice as any) || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Phone Number Management</h2>
          <p className="text-muted-foreground text-sm md:text-base">
            Manage system pool and user phone numbers
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            onClick={handleCleanup} 
            variant="outline"
            disabled={cleaningUp}
            data-testid="button-cleanup-numbers"
          >
            {cleaningUp ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Cleaning...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Cleanup Orphaned
              </>
            )}
          </Button>
          <Button 
            onClick={handleClearSyncStatus} 
            variant="outline"
            disabled={clearingSyncStatus}
            data-testid="button-clear-sync-status"
          >
            {clearingSyncStatus ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Clearing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Clear Sync Status
              </>
            )}
          </Button>
          <Button 
            onClick={handleSyncToElevenLabs} 
            variant="outline"
            disabled={syncingToElevenLabs}
            data-testid="button-sync-elevenlabs"
          >
            {syncingToElevenLabs ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <Phone className="h-4 w-4 mr-2" />
                Sync to ElevenLabs
              </>
            )}
          </Button>
          <Button onClick={() => setAddDialogOpen(true)} data-testid="button-add-system-number">
            <Plus className="h-4 w-4 mr-2" />
            Add System Number
          </Button>
        </div>
      </div>

      <AddSystemNumberDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pool Size</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{systemPoolNumbers.length}</div>
            <p className="text-xs text-muted-foreground">
              System phone numbers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalPurchaseCost.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              One-time purchase cost
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Cost</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalMonthlyCost.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              Recurring monthly cost
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="system" className="space-y-4">
        <TabsList>
          <TabsTrigger value="system">System Pool ({systemPoolNumbers.length})</TabsTrigger>
          <TabsTrigger value="user">User Numbers ({userNumbers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="system">
          <div className="rounded-md border">
            <Table>
              <TableCaption>System phone numbers for free plan users</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Purchase Price</TableHead>
                  <TableHead>Monthly Price</TableHead>
                  <TableHead>Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {systemPoolNumbers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      No system phone numbers in pool
                    </TableCell>
                  </TableRow>
                ) : (
                  systemPoolNumbers.map((phone) => (
                    <TableRow key={phone.id}>
                      <TableCell className="font-mono">{phone.phoneNumber}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">System Pool</Badge>
                      </TableCell>
                      <TableCell>${phone.purchasePrice ? parseFloat(phone.purchasePrice as any).toFixed(2) : "0.00"}</TableCell>
                      <TableCell>${phone.monthlyPrice ? parseFloat(phone.monthlyPrice as any).toFixed(2) : "0.00"}</TableCell>
                      <TableCell>
                        {formatDate(phone.createdAt, "MMM d, yyyy")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="user">
          <div className="rounded-md border">
            <Table>
              <TableCaption>Phone numbers purchased by Pro users</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Purchase Price</TableHead>
                  <TableHead>Monthly Price</TableHead>
                  <TableHead>Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userNumbers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      No user-purchased phone numbers
                    </TableCell>
                  </TableRow>
                ) : (
                  userNumbers.map((phone) => (
                    <TableRow key={phone.id}>
                      <TableCell className="font-mono">{phone.phoneNumber}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{phone.userEmail || "Unknown"}</Badge>
                      </TableCell>
                      <TableCell>${phone.purchasePrice ? parseFloat(phone.purchasePrice as any).toFixed(2) : "0.00"}</TableCell>
                      <TableCell>${phone.monthlyPrice ? parseFloat(phone.monthlyPrice as any).toFixed(2) : "0.00"}</TableCell>
                      <TableCell>
                        {formatDate(phone.createdAt, "MMM d, yyyy")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}